import React, { useState } from 'react';
import {
  Form,
  Button,
  InputNumber,
  Select,
  Row,
  Col,
  Checkbox,
  Space,
  Typography,
} from 'antd';
import Table from 'components/Table';
import RemoveInput from 'components/RemoveInput';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import DrawerForm from 'components/DrawerForm';
import MetricLabel from 'components/MetricLabel';
import DimensionLabel from 'components/DimensionLabel';
import MetricConfig from './MetricConfig';
import DimensionConfig from './DimensionConfig';
import MetricPickerDrawer from './MetricPickerDrawer';
import DimensionPickerDrawer from './DimensionPickerDrawer';

const LabelColumn = ({ value, getLabel }) => getLabel(value);

const getMetricsColumns = () => [
  {
    title: 'Metric',
    dataIndex: 'metric',
    render: (_, field) => (
      <Form.Item name={field.name}>
        <LabelColumn getLabel={m => (
          m?.metric === 'custom'
            ? <MetricLabel name={m?.name} lineBreaks={false} />
            : <MetricLabel metric={m?.metric} lineBreaks={false} />
        )}
        />
      </Form.Item>
    ),
  },
  {
    title: 'Label',
    dataIndex: 'label',
    render: (_, field) => (
      <Form.Item name={field.name}>
        <LabelColumn getLabel={m => (
          m?.label
            ? <MetricLabel label={m.label} lineBreaks={false} />
            : <Typography.Text type="secondary">-</Typography.Text>
        )}
        />
      </Form.Item>
    ),
  },
];

const getDimensionsColumns = () => [
  {
    title: 'Dimension',
    dataIndex: 'name',
    render: (_, field) => (
      <Form.Item name={field.name}>
        <LabelColumn getLabel={d => (
          d?.dimension === 'custom'
            ? <DimensionLabel name={d?.name} lineBreaks={false} />
            : <DimensionLabel dimension={d?.dimension} lineBreaks={false} />
        )}
        />
      </Form.Item>
    ),
  },
  {
    title: 'Label',
    dataIndex: 'label',
    render: (_, field) => (
      <Form.Item name={field.name}>
        <LabelColumn getLabel={d => (
          d?.label
            ? <DimensionLabel label={d.label} lineBreaks={false} />
            : <Typography.Text type="secondary">-</Typography.Text>
        )}
        />
      </Form.Item>
    ),
  },
];

const ResultsConfig = ({ customerId }) => {
  const [editMetric, setEditMetric] = useState(null);
  const [metricPickerState, setMetricPickerState] = useState(null);
  const [editDimension, setEditDimension] = useState(null);
  const [dimensionPickerOpen, setDimensionPickerOpen] = useState(false);

  const metrics = Form.useWatch('metrics') || [];
  const dimensions = Form.useWatch('dimensions');
  const resultType = Form.useWatch(['settings', 'type']);
  const form = Form.useFormInstance();

  const resetMetricPicker = () => {
    setMetricPickerState(null);
  };

  const resetDimensionPicker = () => {
    setDimensionPickerOpen(false);
  };

  const onSelectMetric = (metric) => {
    if (!metricPickerState) return;

    if (metricPickerState.mode === 'add') {
      const nextMetrics = [...metrics, { metric }];
      form.setFields([{
        name: 'metrics',
        touched: true,
        value: nextMetrics,
      }]);

      resetMetricPicker();
      if (metric === 'custom') {
        setEditMetric(nextMetrics.length - 1);
      }
    }
  };

  const onSelectDimension = (dimension) => {
    const nextDimensions = [...dimensions, { dimension }];
    form.setFields([{
      name: 'dimensions',
      touched: true,
      value: nextDimensions,
    }]);

    resetDimensionPicker();
    if (dimension === 'custom') {
      setEditDimension(nextDimensions.length - 1);
    }
  };

  return (
    <>
      <Form.Item>
        <Form.Item>
          <Form.List name="dimensions">
            {(rows, { remove, move }) => (
              <>
                <Table
                  emptyMessage="No dimensions"
                  pagination={false}
                  onSortEnd={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
                  columns={getDimensionsColumns()}
                  dataSource={rows}
                  rowKey="name"
                  interactive={false}
                  actions={(field, index) => (
                    <>
                      <Button icon={<EditOutlined />} onClick={() => setEditDimension(index)}>Edit</Button>
                      <RemoveInput onRemove={() => remove(field.name)} />
                    </>
                  )}
                />
                <Button style={{ marginTop: 16 }} onClick={() => setDimensionPickerOpen(true)} icon={<PlusOutlined />}>Add dimension</Button>
              </>
            )}
          </Form.List>
        </Form.Item>
        <Form.List name="metrics">
          {(rows, { remove, move }) => (
            <>
              <Table
                emptyMessage="No metrics"
                pagination={false}
                onSortEnd={({ oldIndex, newIndex }) => move(oldIndex, newIndex)}
                columns={getMetricsColumns()}
                dataSource={rows}
                rowKey="key"
                interactive={false}
                actions={(field, index) => (
                  <>
                    <Button icon={<EditOutlined />} onClick={() => setEditMetric(index)}>Edit</Button>
                    <RemoveInput onRemove={() => remove(field.name)} />
                  </>
                )}
              />
              <Button style={{ marginTop: 16 }} onClick={() => setMetricPickerState({ mode: 'add' })} icon={<PlusOutlined />}>Add metric</Button>
            </>
          )}
        </Form.List>
      </Form.Item>
      <Row gutter={16}>
        <Col>
          <Form.Item name={['settings', 'type']} label="Type">
            <Select options={[
              { label: 'Table', value: 'table' },
              { label: 'Nested table', value: 'nestedTable' },
              { label: 'Widget', value: 'widget' },
              { label: 'Bar chart', value: 'barChart' },
              { label: 'Stacked bar chart', value: 'stackedBarChart' },
              { label: 'Pie chart', value: 'pieChart' },
              { label: 'Line chart', value: 'lineChart' },
              { label: 'Area chart', value: 'areaChart' },
            ]}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item name={['settings', 'rowLimit']} label="Maximum rows">
            <InputNumber min={1} max={1000} />
          </Form.Item>
        </Col>

      </Row>
      <Row gutter={16}>
        <Col>
          <Form.Item name={['settings', 'pageLimit']} label="Table page size">
            <Select options={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
              { label: 'All', value: null },
            ]}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name={['settings', 'lineChartHeight']} label="Line chart height">
            <InputNumber placeholder="500" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name={['settings', 'pieChartHeight']} label="Pie chart height">
            <InputNumber placeholder="300" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item>
        <Space direction="vertical">
          <Form.Item name={['settings', 'dateComparisonsAsSegments']} valuePropName="checked" noStyle>
            <Checkbox>Show date comparisons as individual segments</Checkbox>
          </Form.Item>
          <Form.Item name={['settings', 'showChangeOverTime']} valuePropName="checked" noStyle>
            <Checkbox>Show diff from comparison dates</Checkbox>
          </Form.Item>
          <Form.Item name={['settings', 'verticalBars']} valuePropName="checked" noStyle>
            <Checkbox>Vertical bars</Checkbox>
          </Form.Item>
          <Form.Item name={['settings', 'hidePresentationSwitcher']} valuePropName="checked" noStyle>
            <Checkbox>Hide presentation switcher</Checkbox>
          </Form.Item>
          <Form.Item name={['settings', 'hidePresentationSettings']} valuePropName="checked" noStyle>
            <Checkbox>Hide presentation settings</Checkbox>
          </Form.Item>
          <Form.Item name={['settings', 'hideLabels']} valuePropName="checked" noStyle>
            <Checkbox>Hide labels</Checkbox>
          </Form.Item>
          <Form.Item name={['settings', 'shareOfTotal']} valuePropName="checked" noStyle>
            <Checkbox>Share of total</Checkbox>
          </Form.Item>
          <Form.Item name={['settings', 'ignoreDimensionCasing']} valuePropName="checked" noStyle>
            <Checkbox>Ignore casing for dimension values</Checkbox>
          </Form.Item>
          {resultType === 'table' && (dimensions?.length ?? 0) > 1 && (
            <Form.Item name={['settings', 'switchDimensions']} valuePropName="checked" noStyle>
              <Checkbox>Show one dimension at a time</Checkbox>
            </Form.Item>
          )}
        </Space>
      </Form.Item>
      <DrawerForm
        title="Edit metric"
        open={editMetric != null}
        onCancel={() => setEditMetric(null)}
        onSubmit={(values) => {
          form.setFields([{
            name: 'metrics',
            touched: true,
            value: metrics.map((m, i) => (i === editMetric ? values : m)),
          }]);
          setEditMetric(null);
        }}
        initialValues={editMetric != null && metrics[editMetric]}
        successMessage={false}
        doneLabel="Close"
      >
        <MetricConfig />
      </DrawerForm>
      <DrawerForm
        title="Edit dimension"
        open={editDimension != null}
        onCancel={() => setEditDimension(null)}
        onSubmit={(values) => {
          form.setFields([{
            name: 'dimensions',
            touched: true,
            value: dimensions.map((d, i) => (i === editDimension ? values : d)),
          }]);
          setEditDimension(null);
        }}
        initialValues={editDimension != null && dimensions[editDimension]}
        successMessage={false}
        doneLabel="Close"
      >
        <DimensionConfig />
      </DrawerForm>
      <MetricPickerDrawer
        open={!!metricPickerState}
        mode={metricPickerState?.mode}
        onClose={resetMetricPicker}
        onSelectMetric={onSelectMetric}
      />
      <DimensionPickerDrawer
        open={dimensionPickerOpen}
        onClose={resetDimensionPicker}
        onSelectDimension={onSelectDimension}
      />
    </>
  );
};

export default ResultsConfig;
